import { CourseQuotes, pickForCourse } from "@/components/Testimonials";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { PublicHeader } from "@/components/PublicHeader";
import { Button } from "@/components/ui/button";
import { BookOpen, PlaySquare, Layers, ArrowRight, LogIn, MessageCircle, Award } from "lucide-react";
import { useSite } from "@/components/useSite";
import { Skeleton } from "@/components/ui/skeleton";
import { useModalityTab, isPresencial, PresencialTag } from "@/components/Presencial";

export default function PublicCourses() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState(null);
  const site = useSite() || {};

  useEffect(() => {
    api.get("/public/courses").then((r) => setCourses(r.data)).catch(() => setCourses([]));
  }, []);

  const wpp = (site.whatsapp || "").replace(/\D/g, "");
  // presenciais em ordem de data (o próximo primeiro); online na ordem do cadastro
  const ordenados = (courses || []).slice().sort((a, b) => (isPresencial(a) && isPresencial(b) ? (a.event_date || "").localeCompare(b.event_date || "") : 0));
  const { tabs, list: lista } = useModalityTab(ordenados, "iep-vitrine-aba");

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicHeader />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg,#1A2340,#232D4F 55%,#1E9E2A)" }}>
        <img src={site.hero_image_url || site.login_banner_url || "/brand/mockup02.webp"} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative z-10 max-w-6xl mx-auto px-5 py-16 sm:py-20 text-center">
          <div className="inline-flex bg-white rounded-xl px-3 py-2 shadow-md mb-6">
            <img src="/brand/logo-horizontal.webp" alt="IEP – In'Nova Educação Profissional" className="h-9 w-auto" />
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold text-white max-w-3xl mx-auto leading-tight">
            {site.tagline || "Sua jornada de aprendizado profissional começa aqui."}
          </h1>
          <p className="text-slate-100/85 mt-4 max-w-2xl mx-auto">
            {site.subtitle || "Conheça os cursos do IEP – In'Nova Educação Profissional."}
          </p>
          <div className="flex items-center justify-center gap-3 mt-8 flex-wrap">
            <Button onClick={() => navigate("/login")} className="h-12 px-6 text-white font-semibold" style={{ background: "#1E9E2A" }}>
              Acessar minha conta
            </Button>
            {wpp && (
              <a href={`https://wa.me/55${wpp}`} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="h-12 px-6 bg-white/10 text-white border-white/30 hover:bg-white/20">
                  <MessageCircle className="h-4 w-4 mr-1.5" /> Fale conosco
                </Button>
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Catálogo */}
      <main className="max-w-6xl mx-auto px-5 py-14">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div className="flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-[#232D4F]" />
            <h2 className="font-display text-2xl font-bold text-slate-900">Nossos cursos</h2>
          </div>
          {courses?.length > 0 && tabs}
        </div>

        {courses === null ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-xl" />)}</div>
        ) : courses.length === 0 ? (
          <div className="p-12 text-center rounded-2xl border-2 border-dashed border-slate-200 bg-white">
            <BookOpen className="h-10 w-10 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Em breve novos cursos.</p>
          </div>
        ) : (
          lista.length === 0 ? (
            <div className="p-12 text-center rounded-2xl border-2 border-dashed border-slate-200 bg-white text-slate-500">Nenhum curso nesta categoria no momento.</div>
          ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {lista.map((c) => (
              <div key={c.id} data-testid={`public-course-${c.id}`} className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all overflow-hidden flex flex-col">
                <div className="aspect-[3/2] bg-slate-100 relative overflow-hidden">
                  {c.banner ? (
                    <img src={c.banner} alt={c.title} loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full grid place-items-center" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}><BookOpen className="h-8 w-8 text-white/70" /></div>
                  )}
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-semibold text-white px-2 py-1 rounded-full shadow" style={{ background: "#1E9E2A" }}>
                    <Award className="h-3 w-3" /> Certificado incluso
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  {c.category && <span className="text-[10px] font-mono uppercase tracking-wider text-[#1E9E2A]">{c.category}</span>}
                  <h3 className="font-display font-semibold text-slate-800 mt-1 line-clamp-2">{c.title}</h3>
                  <p className="text-sm text-slate-500 mt-1.5 line-clamp-2 flex-1">{c.description}</p>
                  <CourseQuotes items={pickForCourse(site.testimonials, c.id, { max: 1, maxLen: 140 })} className="mt-3" />
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                    {isPresencial(c) ? (
                      <div className="min-w-0">
                        <PresencialTag c={c} />
                        {c.venue && <div className="text-xs text-slate-400 truncate mt-0.5">{c.venue}</div>}
                      </div>
                    ) : (
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> {c.module_count}</span>
                      <span className="flex items-center gap-1"><PlaySquare className="h-3.5 w-3.5" /> {c.lesson_count}</span>
                      {c.duration && <span>{c.duration}</span>}
                    </div>
                    )}
                    <button onClick={() => navigate("/login")} className="flex items-center gap-1 text-sm font-semibold text-[#232D4F] hover:gap-2 transition-all">
                      Inscrever-se <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )
        )}
      </main>

      {(site.testimonials || []).length > 0 && (
        <section className="bg-white border-t border-slate-200">
          <div className="max-w-6xl mx-auto px-5 py-14">
            <h2 className="font-display text-2xl font-bold text-slate-900 text-center">O que dizem nossos alunos</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
              {site.testimonials.map((t, i) => (
                <div key={i} data-testid={`testimonial-${i}`} className="rounded-xl border border-slate-200 p-5 bg-[#F8FAFC]">
                  <div className="text-[#1E9E2A] text-3xl leading-none font-display">“</div>
                  <p className="text-slate-600 text-sm leading-relaxed -mt-2">{t.text}</p>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-slate-200">
                    <div className="h-9 w-9 rounded-full grid place-items-center text-white text-sm font-bold" style={{ background: "#232D4F" }}>
                      {(t.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-800">{t.name}</div>
                      {t.role && <div className="text-xs text-slate-400">{t.role}</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-slate-200 bg-white py-8 text-center text-sm text-slate-400">
        {new Date().getFullYear()} © IEP – In&apos;Nova Educação Profissional — Desenvolvido por <strong>In&apos;Nova Art and Sciences</strong> | Todos os direitos reservados
      </footer>
    </div>
  );
}
